/**
 * The breadcrumb row: trail (Maritime Breadcrumbs), page subtitle, and the row's right-hand
 * controls.
 *
 * The trail is always rooted at the Agent Hub, so every app page states where it sits —
 * "Home › Agent Hub › Ship Management › Compliance: Survey Monitoring". The category half of
 * that last crumb is real data: /api/sidebar/user-menu returns `category` and `description`
 * per app, which is also where the italic subtitle comes from.
 *
 * The last crumb is the current page: text with aria-current, not a link.
 *
 * The HOME icon is the return to SIYA Chat. It is an external navigation off this app, so it
 * is a plain anchor when `homeHref` is given rather than a router Link. `homeHref` is absent
 * exactly where that return must not be offered — an external node with no
 * <tenant>.siya.com host, an onlyMicroapps account with no chat behind it, and the framed
 * case, where navigating would load the chat INSIDE the chat's own Micro Apps panel. There
 * the icon links to the hub instead, so it is never dead and never navigates somewhere broken.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Breadcrumbs, Text } from '@syia-ai/maritimeai-ui';

export interface Crumb {
  label: string;
  /** Omitted on the final crumb, which is the page you are on. */
  to?: string;
}

interface BreadcrumbProps {
  crumbs: Crumb[];
  subtitle?: string;
  /** Vessel selector, app switcher — whatever the row carries on the right. */
  actions?: ReactNode;
  /** External URL for the home icon — the SIYA chat for this tenant. Absent → the hub. */
  homeHref?: string;
}

/** The design's home glyph is filled; lucide's is outlined. */
function HomeIcon() {
  return (
    <svg className="shell-crumbs__home-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  );
}

export default function Breadcrumb({ crumbs, subtitle, actions, homeHref }: BreadcrumbProps) {
  const homeIcon = <HomeIcon />;

  return (
    <div className="shell-crumbrow">
      <div className="shell-crumbrow__main">
        <Breadcrumbs className="shell-crumbs" separator={<ChevronRight className="shell-crumbs__sep" />}>
          <Breadcrumbs.Item>
            {homeHref ? (
              <a href={homeHref} aria-label="Back to SIYA Chat">
                {homeIcon}
              </a>
            ) : (
              <Link to="/agent-hub" aria-label="Agent Hub">
                {homeIcon}
              </Link>
            )}
          </Breadcrumbs.Item>

          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            return isLast || !crumb.to ? (
              <Breadcrumbs.Item key={`${crumb.label}-${i}`} current={isLast}>
                {crumb.label}
              </Breadcrumbs.Item>
            ) : (
              <Breadcrumbs.Item key={`${crumb.label}-${i}`}>
                <Link to={crumb.to}>{crumb.label}</Link>
              </Breadcrumbs.Item>
            );
          })}
        </Breadcrumbs>

        {subtitle && <Text className="shell-crumbrow__subtitle">{subtitle}</Text>}
      </div>

      {actions && <div className="shell-crumbrow__actions">{actions}</div>}
    </div>
  );
}
